import React, { useEffect, useRef, useState } from 'react';
import DatePicker from 'react-datepicker';
import CloseIcon from '../../images/CloseIcon';
import { changeProductPriceBulk } from '../../services/items.services';
import { showToast } from '../../services/toast.service';
import {
  BORDER,
  BORDER_SOFT,
  BORDER_STRONG,
  CURRENCY_SYMBOL,
  DESTRUCTIVE,
  Icon,
  MONO,
  TEAL,
  TEAL_DARK,
  TEXT,
  TEXT_DIM,
  TEXT_FAINT,
  TEXT_MUTED,
  currentPriceOf,
  formatMoney,
  t,
  toLocalDay,
} from './itemsListShared';

interface BulkSetPriceDialogProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called after a successful (or partially successful) save. */
  onSaved: () => void;
  /** The full product records behind the current selection. */
  products: any[];
  language: any;
  isMobile: boolean;
}

/** How many preview rows fit before the list collapses into "+ N more". */
const PREVIEW_ROWS = 3;

/**
 * Server skip reasons mapped to copy the user can act on. The API reports
 * `skipped: [{ productId, reason }]` where `reason` is the message of the
 * refusal thrown by ProductPriceService.changePrice — not-found (also covers
 * another company's id) or no active price row. Anything unmapped is shown
 * verbatim.
 */
const REASON_COPY: Record<string, string> = {
  'Product not found': 'they no longer exist or belong to another company',
  'This item has no price configured yet. Set the first price from the item editor.':
    'they have no price yet',
};

/**
 * "Set price" over a selection: one price, applied to every selected item that
 * already has one. Items that have never been priced are never sent — the API
 * creates a NEW price record and end-dates the current one, so there is
 * nothing to supersede for an unpriced item. The dialog says so up-front
 * rather than silently dropping them.
 */
const BulkSetPriceDialog: React.FC<BulkSetPriceDialogProps> = ({
  isOpen,
  onClose,
  onSaved,
  products,
  language,
  isMobile,
}) => {
  const [value, setValue] = useState<string>('');
  const [when, setWhen] = useState<'now' | 'date'>('now');
  const [date, setDate] = useState<Date | null>(null);
  const [error, setError] = useState<string>('');
  const [saving, setSaving] = useState<boolean>(false);
  const priceInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (isOpen) {
      setValue('');
      setWhen('now');
      setDate(null);
      setError('');
      setSaving(false);
      // The dialog is always mounted, so `autoFocus` would only fire at page
      // load (stealing focus from the list) and never on open. Focus the price
      // box when the dialog actually opens — desktop only, a bottom sheet
      // should not pop the soft keyboard.
      if (!isMobile) priceInputRef.current?.focus();
    }
  }, [isOpen]);

  const priced = products.filter((p: any) => currentPriceOf(p) !== null);
  const unpriced = products.filter((p: any) => currentPriceOf(p) === null);
  const newPriceNum = Number(String(value).trim());
  // Mirrors the API DTO: @IsNumber({ maxDecimalPlaces: 2 }) @Min(0). Rejecting
  // 9.999 here beats a bare "Validation failed" from the server.
  const newPriceValid =
    String(value).trim() !== '' &&
    Number.isFinite(newPriceNum) &&
    newPriceNum >= 0 &&
    /^\d+(\.\d{1,2})?$/.test(String(value).trim());

  const close = () => {
    if (!saving) onClose();
  };

  const save = async () => {
    if (saving) return;
    if (priced.length === 0) return;
    if (!newPriceValid) {
      setError(t(language, 'Enter a valid price.', 'Enter a valid price.'));
      return;
    }
    if (when === 'date' && !date) {
      setError(t(language, 'Choose a date.', 'Choose a date.'));
      return;
    }
    setSaving(true);
    try {
      const response: any = await changeProductPriceBulk({
        productIds: priced.map((p: any) => String(p.id)),
        newPrice: newPriceNum,
        // Send the picked LOCAL calendar day, never toISOString(): for a
        // positive-UTC user today's local midnight serialises to yesterday in
        // UTC and the server rejects "today" as past.
        effectiveFrom: when === 'date' && date ? toLocalDay(date) : null,
      });
      // POST /product-price/change-bulk →
      //   data: { updated: [{ productId, previousPriceId }], skipped: [{ productId, reason }] }
      const payload: any = response?.data?.data ?? response?.data ?? {};
      const updatedItems: any[] = Array.isArray(payload?.updated) ? payload.updated : [];
      const skippedItems: any[] = Array.isArray(payload?.skipped) ? payload.skipped : [];
      const updated = updatedItems.length;
      const skipped = skippedItems.length;

      const lines: string[] = [];
      if (skipped > 0) {
        const reasons = Array.from(
          new Set(skippedItems.map((s: any) => String(s?.reason ?? '')).filter(Boolean))
        );
        const reasonText = reasons
          .map(code => t(language, REASON_COPY[code] ?? code, REASON_COPY[code] ?? code))
          .join('; ');
        lines.push(
          `${updated} ${t(language, 'updated', 'updated')} · ${skipped} ${t(
            language,
            'skipped',
            'skipped'
          )}${reasonText ? ` — ${reasonText}` : ''}`
        );
      }
      if (unpriced.length > 0) {
        lines.push(
          `${unpriced.length} ${t(
            language,
            'items had no price yet and were left unchanged',
            'items had no price yet and were left unchanged'
          )}`
        );
      }

      showToast({
        message:
          skipped > 0
            ? t(language, 'Prices partly updated', 'Prices partly updated')
            : t(language, 'Prices updated !', 'Prices updated !'),
        desc: lines.length
          ? `<p class='font-inter text-[14px] text-main leading-6'>${lines.join('<br/>')}</p>`
          : undefined,
        type: skipped > 0 ? 'warning' : 'success',
        duration: skipped > 0 ? 10000 : 5000,
      });
      onClose();
      onSaved();
    } catch (err: any) {
      // Unexpected server errors (5xx) stop the batch. Each item commits on
      // its own, so the API hands back { updated, skipped } for the items it
      // did process — those are re-priced and must not be re-sent blindly.
      const body: any = err?.response?.data ?? {};
      const done: any[] = Array.isArray(body?.updated) ? body.updated : [];
      if (done.length > 0) {
        showToast({
          message: t(language, 'Prices partly updated', 'Prices partly updated'),
          desc: `<p class='font-inter text-[14px] text-main leading-6'>${done.length} ${t(
            language,
            'updated',
            'updated'
          )} · ${t(language, 'Price update failed.', 'Price update failed.')}</p>`,
          type: 'warning',
          duration: 10000,
        });
        onClose();
        onSaved();
        return;
      }
      const serverMsg = typeof body?.message === 'string' ? body.message : null;
      setError(serverMsg ?? t(language, 'Price update failed.', 'Price update failed.'));
    } finally {
      setSaving(false);
    }
  };

  const panelStyle: React.CSSProperties = isMobile
    ? {
        width: '100%',
        maxHeight: '92%',
        backgroundColor: '#FFFFFF',
        borderRadius: '22px 22px 0 0',
        padding: '8px 20px 20px',
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'auto',
        boxShadow: '0 -12px 40px rgba(0,0,0,.12)',
        transition: 'transform 0.3s ease, opacity 0.3s ease',
      }
    : {
        width: '530px',
        maxHeight: '92%',
        backgroundColor: '#FFFFFF',
        borderRadius: '24px',
        padding: '24px',
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'auto',
        boxShadow: '0 24px 60px rgba(15,118,121,.18)',
        transition: 'transform 0.3s ease, opacity 0.3s ease',
      };

  const segment = (label: string, active: boolean, onClick: () => void) => (
    <div
      className="cursor-pointer font-inter"
      style={{
        padding: '8px 20px',
        borderRadius: '30px',
        backgroundColor: active ? TEAL_DARK : '#FFFFFF',
        border: `1px solid ${active ? TEAL_DARK : BORDER_STRONG}`,
        color: active ? '#FFFFFF' : TEXT,
        fontSize: '14px',
        fontWeight: 500,
        whiteSpace: 'nowrap',
      }}
      onClick={onClick}
    >
      {label}
    </div>
  );

  const fieldLabel = (label: string, required?: boolean) => (
    <div
      className="font-inter"
      style={{ fontSize: '14px', fontWeight: 500, color: TEXT, marginBottom: '8px' }}
    >
      {label}
      {required && <span style={{ color: DESTRUCTIVE }}> *</span>}
    </div>
  );

  return (
    <div
      className={`fixed top-0 left-0 flex ${
        isMobile ? 'items-end' : 'items-center px-4'
      } justify-center ${
        isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
      } transition-opacity duration-300 ease-in-out`}
      style={{
        backgroundColor: '#22252552',
        zIndex: 200,
        visibility: isOpen ? 'visible' : 'hidden',
        transition: 'opacity 0.3s ease, visibility 0.3s ease',
        width: '100vw',
        height: '100vh',
      }}
    >
      <div
        className={`transform transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-y-0' : 'translate-y-4 opacity-0'
        }`}
        style={panelStyle}
      >
        {isMobile && (
          <div className="flex justify-center" style={{ padding: '2px 0 10px' }}>
            <div
              style={{
                width: '40px',
                height: '4px',
                borderRadius: '999px',
                backgroundColor: '#DBE4E4',
              }}
            />
          </div>
        )}
        <div className="flex items-start justify-between" style={{ flex: 'none' }}>
          <div>
            <div
              className="font-inter"
              style={{ fontSize: '20px', lineHeight: '24px', fontWeight: 600, color: TEXT }}
            >
              {t(language, 'Set price', 'Set price')}
            </div>
            <div
              className="font-inter"
              style={{ fontSize: '14px', lineHeight: '24px', color: TEXT_DIM, marginTop: '4px' }}
            >
              {t(
                language,
                'One price, applied to every selected item',
                'One price, applied to every selected item'
              )}
            </div>
          </div>
          <div
            onClick={close}
            className="flex items-center justify-center cursor-pointer"
            style={{ width: '24px', height: '24px' }}
          >
            <CloseIcon />
          </div>
        </div>

        <div
          className="flex items-center justify-between"
          style={{
            padding: '12px 16px',
            backgroundColor: BORDER_SOFT,
            borderRadius: '12px',
            marginTop: '16px',
            flex: 'none',
          }}
        >
          <span className="font-inter" style={{ fontSize: '14px', color: TEXT_MUTED }}>
            {t(language, 'Selected', 'Selected')}
          </span>
          <span
            className="font-inter"
            style={{ fontSize: '16px', lineHeight: '24px', fontWeight: 600, color: TEXT }}
          >
            {products.length} {t(language, 'items', 'items')} · {priced.length}{' '}
            {t(language, 'priced', 'priced')}
          </span>
        </div>

        <div style={{ marginTop: '16px', flex: 'none' }}>
          {fieldLabel(t(language, 'New price', 'New price'), true)}
          <div
            className="flex items-center"
            style={{
              height: '48px',
              border: `1px solid ${BORDER}`,
              borderRadius: '12px',
              padding: '0 14px',
              gap: '10px',
            }}
          >
            <span className="font-inter" style={{ fontSize: '17px', color: TEXT_DIM }}>
              {CURRENCY_SYMBOL}
            </span>
            <input
              ref={priceInputRef}
              type="number"
              inputMode="decimal"
              step="0.01"
              min="0"
              value={value}
              onChange={e => {
                setValue(e.target.value);
                setError('');
              }}
              onKeyDown={(e: any) => {
                if (e.key === 'Enter') save();
              }}
              className="font-inter bg-transparent outline-none focus-none"
              style={{ fontSize: '17px', fontWeight: 600, flex: 1, minWidth: 0, color: TEXT }}
            />
          </div>
        </div>

        <div style={{ marginTop: '16px', flex: 'none' }}>
          {fieldLabel(t(language, 'Schedule price change', 'Schedule price change'))}
          <div className="flex" style={{ gap: '8px' }}>
            {segment(t(language, 'Now', 'Now'), when === 'now', () => {
              setWhen('now');
              setError('');
            })}
            {segment(t(language, 'On a date', 'On a date'), when === 'date', () => {
              setWhen('date');
              setError('');
            })}
          </div>
          {when === 'date' && (
            <div className="w-full" style={{ marginTop: '8px' }}>
              <DatePicker
                dateFormat="dd-MM-yyyy"
                minDate={new Date()}
                selected={date}
                placeholderText={t(language, 'Choose a date', 'Choose a date')}
                onChange={(d: any) => {
                  setDate(d);
                  setError('');
                }}
                className="text-[14px] font-inter placeholder:text-placeholder text-main border border-[#DBE4E4] p-3 rounded-xl outline-none focus-none bg-transparent w-full"
              />
            </div>
          )}
        </div>

        {priced.length > 0 && (
          <div style={{ marginTop: '16px', display: 'flex', flexDirection: 'column', flex: 'none' }}>
            <div
              className="flex items-baseline justify-between"
              style={{ marginBottom: '4px' }}
            >
              {fieldLabel(t(language, 'Preview', 'Preview'))}
              <span className="font-inter" style={{ fontSize: '12.5px', color: TEXT_DIM }}>
                {priced.length} {t(language, 'items', 'items')}
              </span>
            </div>
            <div
              style={{
                border: `1px solid ${BORDER_SOFT}`,
                borderRadius: '12px',
                padding: '4px 12px',
                overflow: 'hidden',
              }}
            >
              {priced.slice(0, PREVIEW_ROWS).map((product: any) => (
                <div
                  key={String(product.id)}
                  className="flex items-center"
                  style={{ gap: '10px', padding: '8px 0', borderBottom: '1px solid #F1F5F5' }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      className="font-inter"
                      style={{
                        fontSize: '13px',
                        fontWeight: 500,
                        color: TEXT,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {product.productName}
                    </div>
                    <div
                      style={{ fontSize: '11.5px', color: TEXT_FAINT, fontFamily: MONO }}
                    >
                      {product.sku}
                    </div>
                  </div>
                  <span
                    className="font-inter"
                    style={{
                      fontSize: '13px',
                      color: TEXT_FAINT,
                      textDecoration: 'line-through',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {formatMoney(currentPriceOf(product))}
                  </span>
                  <Icon name="chev" color={BORDER_STRONG} size={12} />
                  <span
                    className="font-inter"
                    style={{
                      fontSize: '13.5px',
                      fontWeight: 600,
                      color: '#1F6B4A',
                      fontVariantNumeric: 'tabular-nums',
                      width: '62px',
                      textAlign: 'right',
                    }}
                  >
                    {newPriceValid ? formatMoney(newPriceNum) : '—'}
                  </span>
                </div>
              ))}
              {priced.length > PREVIEW_ROWS && (
                <div
                  className="font-inter"
                  style={{ padding: '8px 0', fontSize: '12.5px', color: TEXT_DIM }}
                >
                  + {priced.length - PREVIEW_ROWS} {t(language, 'more', 'more')}
                </div>
              )}
            </div>
          </div>
        )}

        {unpriced.length > 0 && (
          <div
            className="flex"
            style={{
              marginTop: '12px',
              gap: '10px',
              padding: '10px 14px',
              backgroundColor: '#F6ECD9',
              borderRadius: '12px',
              color: '#7A4E12',
              flex: 'none',
            }}
          >
            <Icon name="bell" color="#7A4E12" size={16} />
            <div className="font-inter" style={{ fontSize: '12.5px', lineHeight: '18px' }}>
              <b>
                {unpriced.length}{' '}
                {unpriced.length === 1
                  ? t(language, 'item has no price yet', 'item has no price yet')
                  : t(language, 'items have no price yet', 'items have no price yet')}
              </b>{' '}
              —{' '}
              {unpriced
                .slice(0, 5)
                .map((p: any) => p.productName)
                .join(', ')}
              {unpriced.length > 5
                ? ` ${t(language, 'and', 'and')} ${unpriced.length - 5} ${t(
                    language,
                    'more',
                    'more'
                  )}`
                : ''}
              .{' '}
              {t(
                language,
                'They stay unchanged; set a first price from the item editor.',
                'They stay unchanged; set a first price from the item editor.'
              )}
            </div>
          </div>
        )}

        <div
          className="flex items-center"
          style={{ marginTop: '10px', gap: '8px', color: TEXT_DIM, flex: 'none' }}
        >
          <Icon name="sync" color={TEXT_DIM} size={15} />
          <span className="font-inter" style={{ fontSize: '12.5px' }}>
            {t(
              language,
              'Tills receive new prices at the next POS sync — tonight 00:00. Previous prices are kept, not overwritten.',
              'Tills receive new prices at the next POS sync — tonight 00:00. Previous prices are kept, not overwritten.'
            )}
          </span>
        </div>

        {error && (
          <p className="error" style={{ marginTop: '12px' }}>
            {error}
          </p>
        )}

        <div
          className="flex items-center justify-end"
          style={{ marginTop: '20px', gap: '12px', flex: 'none' }}
        >
          <div
            onClick={close}
            className="cursor-pointer text-[14px] leading-[22px] font-inter font-medium"
            style={{
              borderRadius: '40px',
              padding: '10px 24px',
              border: `1px solid ${BORDER_STRONG}`,
              color: TEXT,
              opacity: saving ? 0.5 : 1,
              pointerEvents: saving ? 'none' : 'auto',
            }}
          >
            {t(language, 'Cancel', 'Cancel')}
          </div>
          <div
            onClick={save}
            className="text-[14px] leading-[22px] font-inter font-medium"
            style={{
              borderRadius: '40px',
              padding: '10px 24px',
              backgroundColor: saving || priced.length === 0 ? '#9CA3AF' : TEAL,
              color: '#FFFFFF',
              cursor: saving || priced.length === 0 ? 'not-allowed' : 'pointer',
            }}
          >
            {saving
              ? t(language, 'Saving...', 'Saving...')
              : `${t(language, 'Set price on', 'Set price on')} ${priced.length} ${t(
                  language,
                  'items',
                  'items'
                )}`}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkSetPriceDialog;
